
const libPictView = require('pict-view');

/**
 * Data Lake catalog — the #/Datalake/Browse landing.
 *
 * A two-tab catalog of the customer's lake datasets: IMPORTED clones (read-only copies of legacy systems,
 * e.g. walbec's Lastrada, grouped by source system) vs COMPUTED projections (derived from Headlight data,
 * grouped by pipeline / RefreshGroup). Searchable. Selecting a dataset opens its detail view; "New
 * projection" launches the create wizard. All state + behaviour live in DatalakeCatalogProvider; this view
 * derives + repaints declaratively (no HTML in AppData).
 */
class DatalakeCatalog extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.DatalakeCatalogProvider;
	}

	deriveRenderState()
	{
		if (this.provider)
		{
			this.provider.deriveRenderState();
		}
	}

	onBeforeRender(pRenderable)
	{
		this.deriveRenderState();
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = DatalakeCatalog;

module.exports.default_configuration =
{
	ViewIdentifier: 'DatalakeCatalog',
	DefaultRenderable: 'DatalakeCatalogWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.dlc-wrap { padding: 1.25rem 1.5rem 2rem; }
		.dlc-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 1rem; }
		.dlc-kpis { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-bottom: 1rem; }
		.dlc-kpi { display: flex; align-items: center; gap: 0.55rem; background: var(--bulma-scheme-main-bis, #f6f8fb); border: 1px solid var(--bulma-border, #e6e9ef);
			border-radius: 10px; padding: 0.5rem 0.85rem; min-width: 8.5rem; }
		.dlc-kpi-ic { font-size: 1.05rem; }
		.dlc-kpi-val { font-size: 1.15rem; font-weight: 700; line-height: 1; }
		.dlc-kpi-lbl { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--bulma-text-weak, #7a7a7a); }
		.dlc-toolbar { display: flex; align-items: center; gap: 1rem; margin-bottom: 0.75rem; flex-wrap: wrap; }
		.dlc-tabs { flex: 0 0 auto; }
		.dlc-search { flex: 1 1 16rem; max-width: 24rem; }
		.dlc-group { margin-bottom: 1.1rem; }
		.dlc-group-head { display: flex; align-items: baseline; gap: 0.6rem; padding: 0.3rem 0.2rem; border-bottom: 1px solid var(--bulma-border, #ededed); margin-bottom: 0.4rem; }
		.dlc-group-title { font-weight: 700; }
		.dlc-group-meta { font-size: 0.76rem; color: var(--bulma-text-weak, #7a7a7a); }
		.dlc-row { display: flex; align-items: center; gap: 0.75rem; padding: 0.6rem 0.7rem; border-radius: 8px; cursor: pointer; }
		.dlc-row:hover { background: var(--bulma-scheme-main-bis, #f2f5f9); }
		.dlc-row-main { flex: 1 1 auto; min-width: 0; }
		.dlc-row-name { font-weight: 600; }
		.dlc-row-sub { font-size: 0.78rem; color: var(--bulma-text-weak, #7a7a7a); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.dlc-row-sub code { color: var(--bulma-text-weak, #8a93a3); }
		.dlc-row-cadence { flex: 0 0 auto; font-size: 0.76rem; color: var(--bulma-text-weak, #7a7a7a); }
		.dlc-empty { padding: 2.5rem 1rem; }
		.dlc-spinner { display: inline-block; width: 1.6rem; height: 1.6rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #156dd1); border-radius: 50%; animation: dlc-spin 0.8s linear infinite; }
		@keyframes dlc-spin { to { transform: rotate(360deg); } }
	`,

	Templates:
	[
		{
			Hash: 'Datalake-Catalog-Wrap',
			Template: /*html*/`
				<div class="dlc-wrap">
					<div class="dlc-header">
						<div>
							<h1 class="title is-4"><span class="icon"><i class="fas fa-water"></i></span> Data Lake</h1>
							<p class="subtitle is-6 has-text-grey">Browse the datasets in your private data lake — imported copies of source systems and computed projections of your Headlight data — and create new projections from the Headlight data model.</p>
						</div>
						<button class="button is-link" onclick="_Pict.providers.DatalakeCatalogProvider.newProjection()"><span class="icon"><i class="fas fa-plus"></i></span><span>New projection</span></button>
					</div>

					<div class="dlc-kpis">{~TS:Datalake-Kpi:AppData.DatalakeCatalog.Kpis~}</div>

					{~TS:Datalake-Error:AppData.DatalakeCatalog.ErrorSlot~}

					<div class="dlc-toolbar">
						<div class="tabs is-toggle is-small dlc-tabs">
							<ul>
								<li class="{~D:AppData.DatalakeCatalog.ComputedTabClass~}"><a onclick="_Pict.providers.DatalakeCatalogProvider.navigateKind('computed')"><span class="icon is-small"><i class="fas fa-diagram-project"></i></span><span>Projections ({~D:AppData.DatalakeCatalog.ComputedCount~})</span></a></li>
								<li class="{~D:AppData.DatalakeCatalog.ImportedTabClass~}"><a onclick="_Pict.providers.DatalakeCatalogProvider.navigateKind('imported')"><span class="icon is-small"><i class="fas fa-box-archive"></i></span><span>Imported ({~D:AppData.DatalakeCatalog.ImportedCount~})</span></a></li>
							</ul>
						</div>
						<div class="control has-icons-left dlc-search">
							<input class="input is-small" type="text" placeholder="Search datasets…" value="{~D:AppData.DatalakeCatalog.Search~}" oninput="_Pict.providers.DatalakeCatalogProvider.setSearch(this.value)" />
							<span class="icon is-small is-left"><i class="fas fa-magnifying-glass"></i></span>
						</div>
					</div>

					<div class="box" id="Datalake-Results">
						{~TS:Datalake-Loading:AppData.DatalakeCatalog.LoadingSlot~}
						{~TS:Datalake-Group:AppData.DatalakeCatalog.RailGroups~}
						{~TS:Datalake-Empty:AppData.DatalakeCatalog.EmptySlot~}
					</div>
				</div>`
		},
		{
			// Rendered on its own into #Datalake-Results by setSearch so typing the search box never
			// rebuilds the (sibling) search <input>. Same three slots as the inline block in the Wrap.
			Hash: 'Datalake-Results',
			Template: /*html*/`
				{~TS:Datalake-Loading:AppData.DatalakeCatalog.LoadingSlot~}
				{~TS:Datalake-Group:AppData.DatalakeCatalog.RailGroups~}
				{~TS:Datalake-Empty:AppData.DatalakeCatalog.EmptySlot~}`
		},
		{
			Hash: 'Datalake-Kpi',
			Template: /*html*/`<div class="dlc-kpi"><span class="dlc-kpi-ic" style="color:{~D:Record.Accent~}"><i class="{~D:Record.Icon~}"></i></span><span><span class="dlc-kpi-val">{~D:Record.Value~}</span><br><span class="dlc-kpi-lbl">{~D:Record.Label~}</span></span></div>`
		},
		{
			Hash: 'Datalake-Group',
			Template: /*html*/`
				<div class="dlc-group">
					<div class="dlc-group-head"><span class="dlc-group-title">{~D:Record.GroupLabel~}</span><span class="dlc-group-meta">{~D:Record.Meta~}</span></div>
					{~TS:Datalake-DatasetRow:Record.Items~}
				</div>`
		},
		{
			Hash: 'Datalake-DatasetRow',
			Template: /*html*/`
				<div class="dlc-row" onclick="_Pict.providers.DatalakeCatalogProvider.openItem('{~D:Record.OpenKind~}','{~D:Record.OpenArg~}')">
					<div class="dlc-row-main">
						<div class="dlc-row-name">{~D:Record.Name~}</div>
						<div class="dlc-row-sub"><code>{~D:Record.PhysicalTableName~}</code>{~D:Record.DescriptionSuffix~}</div>
					</div>
					<span class="dlc-row-cadence">{~D:Record.Cadence~}</span>
					<span class="tag {~D:Record.ChipClass~}"><span class="icon is-small"><i class="fas {~D:Record.ChipIcon~}"></i></span><span>{~D:Record.ChipLabel~}</span></span>
					<span class="icon has-text-grey-light"><i class="fas fa-chevron-right"></i></span>
				</div>`
		},
		{
			Hash: 'Datalake-Empty',
			Template: /*html*/`
				<div class="dlc-empty has-text-grey has-text-centered">
					<span class="icon is-large"><i class="fas fa-water fa-2x"></i></span>
					<p class="mt-3">{~D:Record.Message~}</p>
				</div>`
		},
		{
			Hash: 'Datalake-Loading',
			Template: /*html*/`<div class="dlc-empty has-text-grey has-text-centered"><span class="dlc-spinner"></span><p class="mt-3">Loading the data lake catalog…</p></div>`
		},
		{
			Hash: 'Datalake-Error',
			Template: /*html*/`<div class="notification is-danger is-soft py-2 px-3 mb-3"><span class="icon"><i class="fas fa-triangle-exclamation"></i></span> <span>{~D:Record.Message~}</span></div>`
		}
	],

	Renderables:
	[
		{
			RenderableHash: 'DatalakeCatalogWrap',
			TemplateHash: 'Datalake-Catalog-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container'
		}
	]
};
